import React from 'react';
import { graphql } from 'gatsby';
import Accordion from './Accordion';
import ContainedImage from './ContainedImage';
import Content from './Content';
import ContentBlocks from './ContentBlocks';
import ExternalVideo from './ExternalVideo';
import FeaturedBlogPosts from './FeaturedBlogPosts';
import FeaturedCaseStudies from './FeaturedCaseStudies';
import FullWidthImage from './FullWidthImage';
import InternalVideo from './InternalVideo';
import LogoContentBlocks from './LogoContentBlocks';
import MediaContent from './MediaContent';
import NewsletterSignup from './NewsletterSignUp';
import Plans from './Plans';
import PlansOverview from './PlansOverview';
import Tabs from './Tabs';
import Testimonials from './Testimonials';
import TeamProfiles from './TeamProfiles';

const ModularBlocks = ({ items, state, isDetail }) =>
  items.map(item => (
    <React.Fragment key={item.id}>
      {item.model.apiKey === 'accordion_modular_block' && (
        <Accordion
          heading={item.accordionHeading}
          text={item.text}
          items={item.accordionItems}
          link={item.link}
          isDetail={isDetail}
        />
      )}
      {item.model.apiKey === 'contained_image_modular_block' && (
        <ContainedImage image={item.image} isDetail={isDetail} />
      )}
      {item.model.apiKey === 'content_modular_block' && (
        <Content
          heading={item.heading}
          content={item.content}
          twoColumns={item.twoColumns}
          isDetail={isDetail}
        />
      )}
      {item.model.apiKey === 'content_blocks_modular_block' && (
        <ContentBlocks
          heading={item.contentBlocksHeading}
          items={item.contentBlocks}
          isDetail={isDetail}
        />
      )}
      {item.model.apiKey === 'external_video_modular_block' && (
        <ExternalVideo video={item.externalVideo} isDetail={isDetail} />
      )}
      {item.model.apiKey === 'featured_blog_posts_modular_block' && (
        <FeaturedBlogPosts heading={item.heading} items={item.blogPosts} />
      )}
      {item.model.apiKey === 'featured_case_studies_modular_block' && (
        <FeaturedCaseStudies heading={item.heading} items={item.caseStudies} />
      )}
      {item.model.apiKey === 'full_width_image_modular_block' && (
        <FullWidthImage image={item.image} />
      )}
      {item.model.apiKey === 'image_content_modular_block_v1' && (
        <MediaContent
          media={item.image}
          heading={item.heading}
          text={item.text}
          firstLink={item.firstLink}
          secondLink={item.secondLink}
          flip={item.flip}
          version={1}
        />
      )}
      {item.model.apiKey === 'image_content_modular_block_v2' && (
        <MediaContent
          media={item.image}
          heading={item.heading}
          text={item.text}
          firstLink={item.firstLink}
          secondLink={item.secondLink}
          flip={item.flip}
          version={2}
        />
      )}
      {item.model.apiKey === 'internal_video_modular_block' && (
        <InternalVideo
          video={item.internalVideo}
          videoThumbnailSeconds={item.videoThumbnailSeconds}
          isDetail={isDetail}
        />
      )}
      {item.model.apiKey === 'logo_content_blocks_modular_block' && (
        <LogoContentBlocks
          heading={item.heading}
          items={item.logoContentBlocks}
        />
      )}
      {item.model.apiKey === 'newsletter_sign_up_modular_block' && (
        <NewsletterSignup
          heading={item.heading}
          text={item.text}
        />
      )}
      {item.model.apiKey === 'plans_modular_block' && (
        <Plans
          overline={item.overline}
          heading={item.heading}
          ctaLink={item.ctaLink}
          plansLink={item.plansLink}
          state={state}
        />
      )}
      {item.model.apiKey === 'plans_overview_modular_block' && (
        <PlansOverview
          overline={item.overline}
          heading={item.heading}
          ctaLink={item.ctaLink}
          plansLink={item.plansLink}
        />
      )}
      {item.model.apiKey === 'tabs_modular_block' && (
        <Tabs heading={item.heading} items={item.tabs} />
      )}
      {item.model.apiKey === 'team_profiles_modular_block' && (
        <TeamProfiles
          heading={item.heading}
          overline={item.overline}
          items={item.teamDepartments}
        />
      )}
      {item.model.apiKey === 'testimonials_modular_block' && (
        <Testimonials
          items={item.testimonials}
          alt={item.alternativeTheme}
          isDetail={isDetail}
        />
      )}
      {item.model.apiKey === 'video_content_modular_block' && (
        <MediaContent
          media={item.video}
          videoThumbnailSeconds={item.videoThumbnailSeconds}
          heading={item.heading}
          text={item.text}
          firstLink={item.firstLink}
          secondLink={item.secondLink}
          flip={item.flip}
          version={1}
        />
      )}
    </React.Fragment>
  ));

export default ModularBlocks;

export const ModularBlockFragments = graphql`
  fragment AccordionModularBlockFragment on DatoCmsAccordionModularBlock {
    id
    model {
      apiKey
    }
    accordionHeading: heading
    text
    accordionItems {
      heading
      content
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
  }

  fragment ContainedImageModularBlockFragment on DatoCmsContainedImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 1180)
      alt
    }
  }

  fragment ContentModularBlockFragment on DatoCmsContentModularBlock {
    id
    model {
      apiKey
    }
    heading
    content {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    twoColumns
  }

  fragment ContentBlocksModularBlockFragment on DatoCmsContentBlocksModularBlock {
    id
    model {
      apiKey
    }
    contentBlocksHeading: heading
    contentBlocks {
      heading
      text
    }
  }

  fragment ExternalVideoModularBlockFragment on DatoCmsExternalVideoModularBlock {
    id
    model {
      apiKey
    }
    externalVideo {
      height
      width
      provider
      providerUid
      url
    }
  }

  fragment FeaturedBlogPostsModularBlockFragment on DatoCmsFeaturedBlogPostsModularBlock {
    id
    model {
      apiKey
    }
    heading
    blogPosts {
      ...BlogFragment
    }
  }

  fragment FeaturedCaseStudiesModularBlockFragment on DatoCmsFeaturedCaseStudiesModularBlock {
    id
    model {
      apiKey
    }
    heading
    caseStudies {
      ...CaseStudyCardFragment
    }
  }

  fragment FullWidthImageModularBlockFragment on DatoCmsFullWidthImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 2000, layout: FULL_WIDTH)
      alt
    }
  }

  fragment ImageContentModularBlockV1Fragment on DatoCmsImageContentModularBlockV1 {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 710, height: 500)
      alt
    }
    heading
    text
    firstLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    secondLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImageContentModularBlockV2Fragment on DatoCmsImageContentModularBlockV2 {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 870, height: 725)
      alt
    }
    heading
    text
    firstLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    secondLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
  }

  fragment InternalVideoModularBlockFragment on DatoCmsInternalVideoModularBlock {
    id
    model {
      apiKey
    }
    internalVideo {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
    videoThumbnailSeconds
  }

  fragment LogoContentBlocksModularBlockFragment on DatoCmsLogoContentBlocksModularBlock {
    id
    model {
      apiKey
    }
    heading
    logoContentBlocks {
      heading
      logo {
        url
        alt
      }
      text
    }
  }

  fragment NewsletterSignUpModularBlockFragment on DatoCmsNewsletterSignUpModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
  }

  fragment PlansModularBlockFragment on DatoCmsPlansModularBlock {
    id
    model {
      apiKey
    }
    overline
    heading
    plansLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
  }

  fragment PlansOverviewModularBlockFragment on DatoCmsPlansOverviewModularBlock {
    id
    model {
      apiKey
    }
    overline
    heading
    ctaLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    plansLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
  }

  fragment TabsModularBlockFragment on DatoCmsTabsModularBlock {
    id
    model {
      apiKey
    }
    heading
    tabs {
      heading
      content
    }
  }

  fragment TeamProfilesModularBlockFragment on DatoCmsTeamProfilesModularBlock {
    id
    model {
      apiKey
    }
    heading
    overline
    teamDepartments {
      name
      teamMembers {
        image {
          gatsbyImageData(width: 460, height: 575)
          alt
        }
        name
        role
        bio
      }
      isVerticalLayout
    }
  }

  fragment TestimonialsModularBlockFragment on DatoCmsTestimonialsModularBlock {
    id
    model {
      apiKey
    }
    testimonials {
      name
      role
      organisation
      image {
        gatsbyImageData(width: 86, height: 86)
        alt
        title
      }
      quote
    }
    alternativeTheme
  }

  fragment VideoContentModularBlockFragment on DatoCmsVideoContentModularBlock {
    id
    model {
      apiKey
    }
    video {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
    videoThumbnailSeconds
    heading
    text
    firstLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    secondLink {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
  }
`;
